import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type Dispatch,
  type SetStateAction,
} from "react";
import { type DposSessionDetails } from "../../services/dposService";
import { type DposSessionMode } from "./dposPaymentSessionUtils";
import {
  clearDposSessionState,
  getDposSessionStorageKey,
  persistDposSessionState,
  readDposSessionState,
} from "./dposFlowPersistence";

type CompletedSessionView = "success" | "receipt";

interface DposAssetOptionLike {
  symbol: string;
}

interface UseDposSessionPersistenceParams {
  businessId: string | null;
  mode: DposSessionMode;
  amount: string;
  acceptedAssetOptions: DposAssetOptionLike[];
  selectedCryptoAsset: string | null;
  completedView: CompletedSessionView;
  isCompletedSession: boolean;
  sessionId: string | null;
  sessionSeed: string | null;
  resetCreateSession: () => void;
  setSessionSeed: Dispatch<SetStateAction<string | null>>;
  setCreatedSession: Dispatch<SetStateAction<DposSessionDetails | null>>;
  setCreationError: Dispatch<SetStateAction<string | null>>;
  setCopied: Dispatch<SetStateAction<boolean>>;
  setIsAssetMenuOpen: Dispatch<SetStateAction<boolean>>;
  setCompletedView: Dispatch<SetStateAction<CompletedSessionView>>;
  setSelectedCryptoAsset: Dispatch<SetStateAction<string | null>>;
  resetStatusTracking: () => void;
}

interface UseDposSessionPersistenceResult {
  sessionStorageKey: string;
  effectiveSelectedCryptoAsset: string | null;
  clearPersistedSession: () => void;
}

export function useDposSessionPersistence({
  businessId,
  mode,
  amount,
  acceptedAssetOptions,
  selectedCryptoAsset,
  completedView,
  isCompletedSession,
  sessionId,
  sessionSeed,
  resetCreateSession,
  setSessionSeed,
  setCreatedSession,
  setCreationError,
  setCopied,
  setIsAssetMenuOpen,
  setCompletedView,
  setSelectedCryptoAsset,
  resetStatusTracking,
}: UseDposSessionPersistenceParams): UseDposSessionPersistenceResult {
  const restoredSessionIdRef = useRef<string | null>(null);

  const sessionStorageKey = useMemo(
    () => getDposSessionStorageKey(businessId, mode, amount),
    [amount, businessId, mode]
  );
  const effectiveSelectedCryptoAsset = useMemo(() => {
    if (selectedCryptoAsset) {
      return selectedCryptoAsset;
    }

    if (mode === "external_wallet") {
      return acceptedAssetOptions[0]?.symbol || null;
    }

    return null;
  }, [acceptedAssetOptions, mode, selectedCryptoAsset]);
  const clearPersistedSession = useCallback(() => {
    clearDposSessionState(sessionStorageKey);
  }, [sessionStorageKey]);

  useEffect(() => {
    const persistedSessionState = readDposSessionState(sessionStorageKey);

    resetStatusTracking();
    restoredSessionIdRef.current = persistedSessionState?.sessionId || null;
    setSessionSeed(persistedSessionState?.sessionId || null);
    setCreatedSession(null);
    setCreationError(null);
    setCopied(false);
    setIsAssetMenuOpen(false);
    setCompletedView(persistedSessionState?.completedView || "success");
    setSelectedCryptoAsset(
      mode === "external_wallet"
        ? persistedSessionState?.selectedCryptoAsset || null
        : null
    );
    resetCreateSession();
  }, [
    mode,
    resetCreateSession,
    resetStatusTracking,
    sessionStorageKey,
    setCompletedView,
    setCopied,
    setCreatedSession,
    setCreationError,
    setIsAssetMenuOpen,
    setSelectedCryptoAsset,
    setSessionSeed,
  ]);

  useEffect(() => {
    const persistedSessionId =
      sessionId || sessionSeed || restoredSessionIdRef.current;

    if (!persistedSessionId) {
      clearPersistedSession();
      return;
    }

    persistDposSessionState(sessionStorageKey, {
      sessionId: persistedSessionId,
      completedView: isCompletedSession ? completedView : "success",
      selectedCryptoAsset:
        mode === "external_wallet" ? effectiveSelectedCryptoAsset : null,
    });
  }, [
    clearPersistedSession,
    completedView,
    effectiveSelectedCryptoAsset,
    isCompletedSession,
    mode,
    sessionId,
    sessionSeed,
    sessionStorageKey,
  ]);

  return {
    sessionStorageKey,
    effectiveSelectedCryptoAsset,
    clearPersistedSession,
  };
}
